/**
 * @file Context: BackgroundImageContext
 * @description Provides a way for components to communicate the current background image or video
 * to the ResponsiveGameWrapper for displaying a blurred background at the viewport level.
 */

import type React from 'react';
import {
  createContext,
  type ReactNode,
  useContext,
  useRef,
  useState,
} from 'react';

interface BackgroundImageContextType {
  backgroundImageUrl: string | null;
  setBackgroundImageUrl: (url: string | null) => void;
  backgroundVideoUrl: string | null;
  setBackgroundVideoUrl: (url: string | null) => void;
  mainVideoRef: React.RefObject<HTMLVideoElement>;
}

const BackgroundImageContext = createContext<
  BackgroundImageContextType | undefined
>(undefined);

interface BackgroundImageProviderProps {
  children: ReactNode;
}

export const BackgroundImageProvider: React.FC<
  BackgroundImageProviderProps
> = ({ children }) => {
  const [backgroundImageUrl, setBackgroundImageUrl] = useState<string | null>(
    null,
  );
  const [backgroundVideoUrl, setBackgroundVideoUrl] = useState<string | null>(
    null,
  );
  const mainVideoRef = useRef<HTMLVideoElement>(null);

  return (
    <BackgroundImageContext.Provider
      value={{
        backgroundImageUrl,
        setBackgroundImageUrl,
        backgroundVideoUrl,
        setBackgroundVideoUrl,
        mainVideoRef,
      }}
    >
      {children}
    </BackgroundImageContext.Provider>
  );
};

export const useBackgroundImage = () => {
  const context = useContext(BackgroundImageContext);
  if (context === undefined) {
    throw new Error(
      'useBackgroundImage must be used within a BackgroundImageProvider',
    );
  }
  return context;
};
